import { useEffect, useRef } from 'react';

// useWakeLock holds a screen wake lock for as long as `active` is true, so the
// phone doesn't dim and lock itself mid-set. That lock is the actual fix for
// "the timer stops when the screen turns off": once iOS locks the screen it
// suspends the page's JS timers within seconds, and nothing we do in a
// setInterval can prevent that. Keeping the screen awake avoids the suspension
// entirely.
//
// It is best-effort, not load-bearing. The browser drops the lock whenever the
// page is hidden (so we re-acquire on visibilitychange), rejects the request
// outright in Low Power Mode, and only supports it at all from Safari 16.4.
// Every timer that uses this hook is also deadline-based, so when the lock is
// unavailable the countdown still reports the right time on return — it just
// won't have been ticking on screen.
// Releasing a lock the OS already revoked can reject, and a bad implementation
// could throw outright. Neither may escape into React's cleanup path, where it
// would blow up an unmount.
function safeRelease(s: WakeLockSentinel): void {
  void Promise.resolve()
    .then(() => s.release())
    .catch(() => undefined);
}

export function useWakeLock(active: boolean): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    const release = () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) safeRelease(s);
    };

    const acquire = async () => {
      if (cancelled || sentinelRef.current) return;
      // A request while hidden always rejects — wait for the page to come back.
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      try {
        const s = await navigator.wakeLock?.request('screen');
        if (!s) return;
        if (cancelled) {
          safeRelease(s);
          return;
        }
        sentinelRef.current = s;
        // The OS can revoke the lock on its own (page hidden, battery saver).
        // Clear our handle so a later re-acquire isn't skipped as a no-op.
        s.addEventListener('release', () => {
          if (sentinelRef.current === s) sentinelRef.current = null;
        });
      } catch {
        // Unsupported or denied. Deadline timers keep correct time regardless.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      release();
    };
  }, [active]);
}
